import { ChangesDetection } from "@adas/ng-overlay";
import { I_TreatmentResponse, I_RoutesResponse, I_OrderResponse, I_TreatmentPlacerOrderResponse, I_TreatmentFillerOrderResponse, I_CodeStatusResponse, I_CodeResponse, I_TreatmentNoteResponse } from "../api/treatment.response";
import { distinctUntilChanged, filter, map, Observable, of, shareReplay, startWith, switchMap } from "rxjs";
import { E_ChangeAction } from "../../../../../enums/change-action.enum";
import { E_OrderControlType } from "../../../../../enums/treatment-order-control.enum";

export class C_Treatment extends ChangesDetection {
    singleDose: boolean = false;
    boloPom: boolean = false;
    id: string = "";
    patientId: string = "";
    // Define el tipo adecuado para orderControl (por ejemplo, enum OrderControlType)
    orderControl!: E_OrderControlType;
    placerOrder: C_TreatmentPlacerOrder | null = null;
    fillerOrder: C_TreatmentFillerOrder | null = null;
    orderStatus: string = "";
    orderTime: string | null = null;
    endTime: string | null = null;
    startTime: string | null = null;
    requestedGiveCodes: C_Code[] = [];
    requestedGiveTreatment: string = "";
    requestedGiveCodesStatus: C_CodeStatus[] = [];
    requestedGiveAmountMinimum: number | null = null;
    requestedGiveAmountMaximum: number | null = null;
    requestedGiveUnits: C_Code | null = null;
    requestedDosageForm: C_Code | null = null;
    notes: C_TreatmentNote[] = [];
    routes: C_Routes[] = [];
    messageTime: string = "";
    systemId: string | null = null;
    canRead: boolean = false;
    canWrite: boolean = false;

    constructor(json?: I_TreatmentResponse) {
        super();
        if (!json) return;
        this.initializeFromJson(json);
    }

    initializeFromJson(json: I_TreatmentResponse) {
        if (json.hasOwnProperty('singleDose')) this.singleDose = json.singleDose;
        if (json.hasOwnProperty('boloPom')) this.boloPom = json.boloPom;
        if (json.id) this.id = json.id;
        if (json.patientId) this.patientId = json.patientId;
        if (json.orderControl) this.orderControl = json.orderControl;
        if (json.placerOrder) this.placerOrder = new C_TreatmentPlacerOrder(json.placerOrder);
        if (json.fillerOrder) this.fillerOrder = new C_TreatmentFillerOrder(json.fillerOrder);
        if (json.orderStatus) this.orderStatus = json.orderStatus;
        if (json.orderTime) this.orderTime = json.orderTime;
        if (json.endTime) this.endTime = json.endTime;
        if (json.startTime) this.startTime = json.startTime;
        if (json.requestedGiveCodes) this.requestedGiveCodes = json.requestedGiveCodes.map(code => new C_Code(code));
        if (json.requestedGiveTreatment) this.requestedGiveTreatment = json.requestedGiveTreatment;
        if (json.requestedGiveCodesStatus) this.requestedGiveCodesStatus = json.requestedGiveCodesStatus.map(status => new C_CodeStatus(status));
        if (json.hasOwnProperty('requestedGiveAmountMinimum')) this.requestedGiveAmountMinimum = json.requestedGiveAmountMinimum;
        if (json.hasOwnProperty('requestedGiveAmountMaximum')) this.requestedGiveAmountMaximum = json.requestedGiveAmountMaximum;
        if (json.requestedGiveUnits) this.requestedGiveUnits = new C_Code(json.requestedGiveUnits);
        if (json.requestedDosageForm) this.requestedDosageForm = new C_Code(json.requestedDosageForm);
        if (json.notes) this.notes = json.notes.map(note => new C_TreatmentNote(note));
        if (json.routes) this.routes = json.routes.map(route => new C_Routes(route));
        if (json.messageTime) this.messageTime = json.messageTime;
        if (json.hasOwnProperty('systemId')) this.systemId = json.systemId;
        this.forceUpdateDetection();
    }

}

export class C_Routes {
    route: C_Code = new C_Code();

    constructor(json?: I_RoutesResponse) {
        if (json?.route) {
            this.route = new C_Code(json.route);
        }
    }
}

export class C_Order {
    entityIdentifier: string = "";
    namespaceID: string = "";

    constructor(json?: I_OrderResponse) {
        if (json) {
            if (json.entityIdentifier) this.entityIdentifier = json.entityIdentifier;
            if (json.namespaceID) this.namespaceID = json.namespaceID;
        }
    }
}

export class C_TreatmentPlacerOrder extends C_Order {
    constructor(json?: I_TreatmentPlacerOrderResponse) {
        super(json);
    }
}

export class C_TreatmentFillerOrder extends C_Order {
    constructor(json?: I_TreatmentFillerOrderResponse) {
        super(json);
    }
}

export class C_CodeStatus {
    code?: C_Code;
    status?: string;
    administrationTime?: string;
    endAdministrationTime?: string;

    constructor(json?: I_CodeStatusResponse) {
        if (!json) return;
        if (json.code) this.code = new C_Code(json.code);
        if (json.status) this.status = json.status;
        if (json.administrationTime) this.administrationTime = json.administrationTime;
        if (json.endAdministrationTime) this.endAdministrationTime = json.endAdministrationTime;
    }
}

export class C_Code {
    identifier: string = "";
    text: string = "";
    codingSystem: string = "";

    constructor(json?: I_CodeResponse) {
        if (!json) return;
        if (json.identifier) this.identifier = json.identifier;
        if (json.text) this.text = json.text;
        if (json.codingSystem) this.codingSystem = json.codingSystem;
    }
}

export class C_TreatmentNote {
    commentType: string = "";
    comment: string = "";
    enteredTime: string = "";

    constructor(json?: I_TreatmentNoteResponse) {
        if (!json) return;
        if (json.commentType) this.commentType = json.commentType;
        if (json.comment) this.comment = json.comment;
        if (json.enteredTime) this.enteredTime = json.enteredTime;
    }
}

export class C_TreatmentList extends ChangesDetection<T_TreatmentListChange> {
    private list: Map<string, Map<string, C_Treatment>> = new Map(); // Nivel 1: PatientId -> Nivel 2: Map<TreatmentId, C_Treatment>
    private cachedSubs = new Map<string, Observable<C_Treatment | undefined>>(); // Cache de observables indexado por una clave compuesta "patientId_treatment"

    constructor() {
        super();
    }

    getValuesFromPatientId(patId: string): C_Treatment[] {
        return Array.from(this.list.get(patId)?.values() || []);
    }

    /**
     * Inserta un tratamiento individual 
     */
    addNewTreatment(treatment: I_TreatmentResponse) {
        const patientId = treatment.patientId;
        const treatmentId = treatment.id;
        if (!patientId || !treatmentId) return;

        let patientMap = this.list.get(patientId);
        if (!patientMap) {
            patientMap = new Map<string, C_Treatment>();
            this.list.set(patientId, patientMap);
        }

        if (!patientMap.has(treatmentId)) {
            const newTreatment = new C_Treatment(treatment);
            patientMap.set(treatmentId, newTreatment);

            this.forceUpdateDetection({
                action: E_ChangeAction.Add,
                patientId,
                treatmentId,
                data: newTreatment,
            });
        } else {
            this.updateTreatment(treatment);
        }
    }

    updateTreatment(treatment: I_TreatmentResponse) {
        const patientId = treatment.patientId;
        const treatmentId = treatment.id;
        if (!patientId || !treatmentId) return;

        const patientMap = this.list.get(patientId);
        if (!patientMap || !patientMap.has(treatmentId)) return;

        patientMap.get(treatmentId)!.initializeFromJson(treatment);

        this.forceUpdateDetection({
            action: E_ChangeAction.Update,
            patientId,
            treatmentId,
            data: treatment,
        });
    }

    /**
         * Inserción masiva optimizada con recolección granular de afectados
         */
    setTreatmentsBulk(treatmentList: (I_TreatmentResponse & { localId: symbol })[]) {
        const affected = new Set<string>();

        treatmentList.forEach(treatment => {
            const treatmentId = treatment.id;
            const patientId = treatment.patientId;
            if (!treatmentId || !patientId) return;

            let patientMap = this.list.get(patientId);
            if (!patientMap) {
                patientMap = new Map<string, C_Treatment>();
                this.list.set(patientId, patientMap);
            }

            // Registrar clave compuesta idéntica a la estructura del caché
            affected.add(`${patientId}_${treatmentId}`);

            if (patientMap.has(treatmentId)) {
                const currentObs: C_Treatment = patientMap.get(treatmentId)!;
                currentObs.initializeFromJson(treatment);
            } else {
                const newObs = new C_Treatment(treatment);
                patientMap.set(treatmentId, newObs);
            }
        });

        this.forceUpdateDetection({ action: E_ChangeAction.AddBulk, affected });
    }


    /**
     * Obtiene el flujo reactivo optimizado descartando colisiones cruzadas de hilos
     */
    getLiveTreatment(patientId: string, treatmentId: string): Observable<C_Treatment | undefined> {
        const cacheKey = `${patientId}_${treatmentId}`;
        if (this.cachedSubs.has(cacheKey)) return this.cachedSubs.get(cacheKey)!;

        const stream$ = this.getForcedChanges().pipe(
            startWith(null),
            filter((change) => {
                if (!change) return true; // Disparo inicial del flujo
                if (change.action === E_ChangeAction.Reset) return true;
                if (change.action === E_ChangeAction.AddBulk) return change.affected.has(cacheKey); // Filtrado granular masivo

                // Filtrado atómico (Add / Update / Delete)
                return change.patientId === patientId && change.treatmentId === treatmentId;
            }),
            map(() => {
                const pMap = this.list.get(patientId);
                return pMap ? pMap.get(treatmentId) : undefined;
            }),
            distinctUntilChanged(),
            switchMap(treatment => {
                if (!treatment) return of(undefined);

                // Escucha de mutaciones internas de la instancia del modelo de datos
                return treatment.getForcedChanges().pipe(
                    startWith(treatment),
                    map(() => treatment)
                );
            }),
            shareReplay({ bufferSize: 1, refCount: true })
        );

        this.cachedSubs.set(cacheKey, stream$);
        return stream$;
    }


    getTreatment(patientId: string, treatmentKey: string): C_Treatment | undefined {
        const treatm = Array.from(this.list.get(patientId)?.values() || []).find(tr => tr.requestedGiveCodes[0].text == treatmentKey);
        return treatm;
    }

    getTreatments(patientId: string, treatmentKey: string): C_Treatment[] {
        const treatm = Array.from(this.list.get(patientId)?.values() || []).filter(tr => tr.requestedGiveCodes[0].text == treatmentKey);
        return treatm;
    }

    clearSubs() {
        this.cachedSubs.clear();
        this.forceUpdateDetection({ action: E_ChangeAction.Reset });
    }
}

export type T_TreatmentListChange =
    | { action: E_ChangeAction.Add; patientId: string; treatmentId: string; data: C_Treatment }
    | { action: E_ChangeAction.Update; patientId: string; treatmentId: string; data: I_TreatmentResponse }
    | { action: E_ChangeAction.Delete; patientId: string; treatmentId: string }
    | { action: E_ChangeAction.AddBulk; affected: Set<string> }
    | { action: E_ChangeAction.Reset };